import React, { useState, useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';

import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  AttachFile as AttachFileIcon,
  Language as LanguageIcon,
  GridViewOutlined as OverviewIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import ArchiveIconSvg from '../components/common/ArchiveIconSvg';
import BulkOrderUploadModal from '../components/orders/BulkOrderUploadModal';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { guestBlogPlacementService, GuestBlogPlacement, GetPlacementsParams } from '../services/guestBlogPlacementService';

import { clientService } from '../services/clientService';

import { projectService } from '../services/projectService';

import { publisherService } from '../services/publisherService';

import { useAuth } from '../contexts/AuthContext';
import { useProjectWorkflowOptional } from '../contexts/ProjectWorkflowContext';
import { useCart } from '../contexts/CartContext';

import RichTextEditor from '../components/common/RichTextEditor';
import DocumentPreviewModal from '../components/common/DocumentPreviewModal';

import { exportToCSV, formatDateForCSV, formatCurrencyForCSV } from '../utils/csvExport';
import ColumnVisibilityMenu from '../components/common/ColumnVisibilityMenu';
import { useColumnVisibility } from '../hooks/useColumnVisibility';

import {
  isAllowedContentDocumentFile,
  CONTENT_DOCUMENT_REJECT_MESSAGE,
} from '../utils/contentDocumentUpload';

const PLACEMENT_YEAR_MIN = 2025;

type PlacementSortOption = 'createdAt_desc' | 'price_desc' | 'orderDate_desc' | 'status_asc';

function parsePlacementSortOption(option: PlacementSortOption): {
  sortBy: GetPlacementsParams['sortBy'];
  sortOrder: GetPlacementsParams['sortOrder'];
} {
  const [sortBy, sortOrder] = option.split('_') as [
    NonNullable<GetPlacementsParams['sortBy']>,
    NonNullable<GetPlacementsParams['sortOrder']>,
  ];
  return { sortBy, sortOrder };
}

function getPlacementContentUrl(placement: GuestBlogPlacement): string {
  return placement.contentUrl?.trim() || '';
}

const GuestBlogPlacementsPage: React.FC = () => {

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('READY_TO_PUBLISH');

  const [clientFilter, setClientFilter] = useState('');

  const [projectFilter, setProjectFilter] = useState('');

  const [publisherFilter, setPublisherFilter] = useState('');

  const [monthFilter, setMonthFilter] = useState<number | undefined>(undefined); // undefined = All months

  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined); // undefined = All years

  const [sortOption, setSortOption] = useState<PlacementSortOption>('createdAt_desc');

  const yearOptions = useMemo(() => {
    const maxYear = new Date().getFullYear();
    const count = Math.max(0, maxYear - PLACEMENT_YEAR_MIN + 1);
    return Array.from({ length: count }, (_, i) => maxYear - i);
  }, []);

  // Sync filters from URL parameters (workflow deep-links). When params are absent
  // — e.g. standalone Orders menu — clear filters so all placements show.
  useEffect(() => {
    const clientId = searchParams.get('clientId');
    const projectId = searchParams.get('projectId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');
    const status = searchParams.get('status');

    setClientFilter(clientId || '');
    setProjectFilter(projectId || '');
    setMonthFilter(month ? parseInt(month, 10) : undefined);
    setYearFilter(year ? parseInt(year, 10) : undefined);
    setStatusFilter(status || 'READY_TO_PUBLISH');
    setPage(0);
  }, [searchParams]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  const [selectedPlacement, setSelectedPlacement] = useState<GuestBlogPlacement | null>(null);

  const [editData, setEditData] = useState({

    status: '',

    liveUrl: '',

    notes: '',

    contentText: '',

    contentUrl: '',

    contentDocUrl: '',

  });

  const [editingContentUrl, setEditingContentUrl] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [uploadingFile, setUploadingFile] = useState(false);

  const [exportingCSV, setExportingCSV] = useState(false);
  const [orderImportOpen, setOrderImportOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({

    open: false,

    message: '',

    severity: 'success' as 'success' | 'error' | 'warning',

  });





  // Get user role from auth context

  const { user, isAdmin, isAgency, isPublisher, isClient, hasPermission } = useAuth();

  // Project workflow context (present only when viewing this page as the
  // "Order Status" step of a project's workflow). Used to show quick-nav buttons.
  const workflow = useProjectWorkflowOptional();
  const urlProjectId = searchParams.get('projectId') || '';
  // Workflow Order Status step only when projectId is in the URL (not standalone menu).
  const inWorkflowStatus = Boolean(urlProjectId && workflow?.activeStep === 'status');
  const workflowProjectId = urlProjectId || workflow?.selectedProject?.id || '';
  const { clearCart, refreshCart } = useCart(workflowProjectId || undefined);

  const handleExploreMoreSites = async () => {
    if (!workflow) return;
    // Start a brand-new selection: clear cart and drop any saved order (planId)
    // from the URL so Explore Sites doesn't resume the latest saved order.
    if (workflowProjectId) {
      await clearCart();
      await refreshCart();
    }
    workflow.goToStep('sites', { fresh: true });
  };

  

  // Temporary fallback for admin detection

  const isSuperAdminFallback = user?.role === 'SUPER_ADMIN' || user?.email === 'superadmin@example.com';

  const isAdminFallback = isAdmin || isSuperAdminFallback;
  const canImportOrders = hasPermission('placements.import_orders');

  const hasPlacementAccess = isAdminFallback || isAgency || isPublisher || isClient;
  const canEditPlacements = !isClient;
  const showActionsColumn = canEditPlacements;

  // Column visibility configuration - role-based
  const getDefaultColumns = () => {
    const baseColumns = [
      { id: 'placementId', label: 'Placement ID', visible: true, required: true },
      { id: 'orderId', label: 'Order ID', visible: true },
      { id: 'siteUrl', label: 'Site URL', visible: true },
      { id: 'content', label: 'Content', visible: true },
      { id: 'liveUrl', label: 'Live URL', visible: true },
      { id: 'client', label: 'Client', visible: true },
      { id: 'project', label: 'Project', visible: true },
      { id: 'orderMonth', label: 'Order Month', visible: true },
      { id: 'price', label: 'GB Price', visible: true },
      { id: 'status', label: 'Status', visible: true },
    ];

    if (!isClient) {
      baseColumns.push({ id: 'actions', label: 'Actions', visible: true, required: true });
    }

    // Only Super Admin sees publisher name
    if (isSuperAdminFallback) {
      baseColumns.splice(7, 0, { id: 'publisherName', label: 'Publisher Name', visible: true });
    }

    return baseColumns;
  };

  const DEFAULT_COLUMNS = getDefaultColumns();

  const { columns, toggleColumn, resetDefaults, isColumnVisible } = useColumnVisibility(
    `guestBlogPlacements_columnVisibility_${user?.role || 'guest'}`,
    DEFAULT_COLUMNS
  );



  // Fetch clients for dropdown

  const { data: clientsData } = useQuery({

    queryKey: ['clients'],

    queryFn: () => clientService.getClients({ limit: 1000 }),

    enabled: isAdminFallback && !isClient, // Don't fetch for CLIENT users - they only see their own data

  });



  const clients = clientsData?.data || [];



  // Fetch projects for dropdown

  const { data: projectsData } = useQuery({

    queryKey: ['projects', clientFilter, inWorkflowStatus],

    queryFn: () => projectService.getProjects({ 

      limit: 1000,

      clientId: clientFilter || undefined,

      hasCompletedCheckout: inWorkflowStatus || undefined,

    }),

    enabled: isAdminFallback || isAgency || isPublisher || isClient, // Enable for Publishers and Clients too

  });



  const projects = projectsData?.data || [];



  // Fetch publishers who have placements (only Super Admin)

  const { data: publishersWithPlacements } = useQuery({

    queryKey: ['publishers-with-placements'],

    queryFn: () => publisherService.getPublishersWithPlacements(),

    enabled: isSuperAdminFallback,

  });



  const publishers = publishersWithPlacements || [];



  // Fetch placements

  const { data: placementsData } = useQuery({

    queryKey: ['placements', page, rowsPerPage, search, statusFilter, clientFilter, projectFilter, publisherFilter, monthFilter, yearFilter, sortOption],

    queryFn: async () => {
      const { sortBy, sortOrder } = parsePlacementSortOption(sortOption);

      const result = await guestBlogPlacementService.getPlacements({

        page: page + 1,

        limit: rowsPerPage,

        search,

        status: statusFilter,

        clientId: clientFilter,

        projectId: projectFilter,

        publisherId: publisherFilter,

        month: monthFilter,

        year: yearFilter,

        sortBy,

        sortOrder,

      });

      return result;

    },

    enabled: hasPlacementAccess,

  });



  const placements = placementsData?.data || [];

  const totalCount = placementsData?.pagination?.total || 0;



  // Fetch all placements for accurate status counts

  const { data: allPlacementsData } = useQuery({

    queryKey: ['all-placements', search, clientFilter, projectFilter, publisherFilter, monthFilter, yearFilter],

    queryFn: async () => {

      const result = await guestBlogPlacementService.getPlacements({

        limit: 10000, // Get all data

        search,

        clientId: clientFilter,

        projectId: projectFilter,

        publisherId: publisherFilter,

        month: monthFilter,

        year: yearFilter,

      });

      return result;

    },

    enabled: hasPlacementAccess,

  });



  const allPlacements = allPlacementsData?.data || [];



  // Update placement mutation

  const updateMutation = useMutation({

    mutationFn: ({ id, data }: { id: string; data: any }) => 

      guestBlogPlacementService.updatePlacement(id, data),

    onSuccess: () => {

      // Invalidate placements cache to refresh table immediately

      queryClient.invalidateQueries({ queryKey: ['placements'] });

      // Invalidate all-placements cache to refresh filter counts

      queryClient.invalidateQueries({ queryKey: ['all-placements'] });

      // Invalidate guest-blog-sites cache to refresh usage statistics

      queryClient.invalidateQueries({ queryKey: ['guest-blog-sites'] });

      setEditDialogOpen(false);

      setSelectedPlacement(null);

      setSnackbar({

        open: true,

        message: 'Placement updated successfully!',

        severity: 'success',

      });

    },

    onError: (error: any) => {

      setSnackbar({

        open: true,

        message: error.response?.data?.error || 'Failed to update placement.',

        severity: 'error',

      });

    },

  });



  // Delete placement mutation

  const deleteMutation = useMutation({

    mutationFn: (id: string) => guestBlogPlacementService.deletePlacement(id),

    onSuccess: () => {

      // Invalidate placements cache to refresh table immediately

      queryClient.invalidateQueries({ queryKey: ['placements'] });

      // Invalidate ordered-sites cache so badges update on Guest Blog Sites page

      queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });

      // Invalidate guest-blog-sites cache to refresh usage statistics

      queryClient.invalidateQueries({ queryKey: ['guest-blog-sites'] });

      setSnackbar({

        open: true,

        message: 'Placement archived successfully!',

        severity: 'success',

      });

    },

    onError: (error: any) => {

      setSnackbar({

        open: true,

        message: error.response?.data?.error || 'Failed to archive placement.',

        severity: 'error',

      });

    },

  });



  const openPlacementDialog = (placement: GuestBlogPlacement, viewOnly: boolean) => {

    setSelectedPlacement(placement);

    setEditData({

      status: placement.status,

      liveUrl: placement.liveUrl || '',

      notes: placement.notes || '',

      contentText: placement.contentText || '',

      contentUrl: placement.contentUrl || '',

      contentDocUrl: placement.contentDocUrl || '',

    });

    setEditingContentUrl(false);
    setIsViewMode(viewOnly || isClient);

    setEditDialogOpen(true);

  };

  const handleEdit = (placement: GuestBlogPlacement) => {
    openPlacementDialog(placement, false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {

    const file = event.target.files?.[0];

    if (!file) return;

    if (!isAllowedContentDocumentFile(file)) {
      setSnackbar({ open: true, message: CONTENT_DOCUMENT_REJECT_MESSAGE, severity: 'error' });
      event.target.value = '';
      return;
    }

    setUploadingFile(true);

    try {

      const response = await guestBlogPlacementService.uploadContentDocument(file);

      setEditData(prev => ({ ...prev, contentDocUrl: response.data.url }));

      setSnackbar({

        open: true,

        message: 'File uploaded successfully!',

        severity: 'success',

      });

    } catch (error: any) {

      setSnackbar({

        open: true,

        message: error.response?.data?.error || 'Failed to upload file.',

        severity: 'error',

      });

    } finally {

      setUploadingFile(false);

      event.target.value = '';

    }

  };



  // Normalize Live URL - add https:// if missing

  const normalizeLiveUrl = (url: string): string => {

    if (!url || url.trim() === '') return '';

    

    const trimmedUrl = url.trim();

    

    // If already has protocol (http:// or https://), return as is

    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {

      return trimmedUrl;

    }

    

    // Add https:// prefix

    return `https://${trimmedUrl}`;

  };



  const handleUpdate = () => {

    if (!selectedPlacement) return;

    
    // Validate: If changing status from READY_TO_PUBLISH to another status, Live URL is required
    // Exception: HOLD status doesn't need Live URL

    const isChangingFromReadyToPublish = selectedPlacement.status === 'READY_TO_PUBLISH' && editData.status !== 'READY_TO_PUBLISH';

    const hasNoLiveUrl = !editData.liveUrl || editData.liveUrl.trim() === '';

    const isChangingToHold = editData.status === 'HOLD';
    const isChangingToMailSent = editData.status === 'MAIL_SENT';

    
    if (isChangingFromReadyToPublish && hasNoLiveUrl && !isChangingToHold && !isChangingToMailSent) {

      setSnackbar({

        open: true,

        message: 'Please enter a Live URL before changing the status from "Ready to Publish"',

        severity: 'warning',

      });

      return;

    }

    

    // Validate: If status is COMPLETED, Live URL is required (HOLD status doesn't need Live URL)

    if (editData.status === 'COMPLETED' && hasNoLiveUrl) {

      setSnackbar({

        open: true,

        message: 'Live URL is required when status is "Completed"',

        severity: 'error',

      });

      return;

    }

    

    // Normalize Live URL before sending

    const normalizedData = {

      ...editData,

      liveUrl: normalizeLiveUrl(editData.liveUrl),

    };

    

    updateMutation.mutate({

      id: selectedPlacement.id,

      data: normalizedData,

    });

  };



  const handleArchive = (placement: GuestBlogPlacement) => {
    if (window.confirm(
      `Archive placement ${placement.placementId}? It will be hidden from lists. Restore within 7 days from Settings → Restore Archived Data.`
    )) {
      deleteMutation.mutate(placement.id);
    }
  };



  const handlePageChange = (event: unknown, newPage: number) => {

    setPage(newPage);

  };



  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);

  };





  // Get available statuses for dropdown (only statuses with filter buttons)

  const getAllStatuses = () => [

    { value: 'READY_TO_PUBLISH', label: 'Ready to Publish' },

    { value: 'FOR_REVIEW', label: 'For Review' },

    { value: 'NEED_CORRECTION', label: 'Need Correction' },

    { value: 'COMPLETED', label: 'Completed' },

    { value: 'MAIL_SENT', label: 'Mail Sent' },

    { value: 'SITE_REPLACED', label: 'Site Replaced' },

    { value: 'HOLD', label: 'Hold' }

  ];



  // Get available statuses for publishers (limited options)

  const getPublisherStatuses = () => {
    const allowedStatuses = [
      { value: 'READY_TO_PUBLISH', label: 'Ready to Publish' },
      { value: 'FOR_REVIEW', label: 'For Review' },
      { value: 'HOLD', label: 'Hold' },
      { value: 'SITE_REPLACED', label: 'Site Replaced' },
    ];

    // If current placement has a status not in allowed list, add it as disabled option
    if (selectedPlacement && selectedPlacement.status) {
      const currentStatusExists = allowedStatuses.some(s => s.value === selectedPlacement.status);
      if (!currentStatusExists) {
        const statusLabels: Record<string, string> = {
          'COMPLETED': 'Completed',
          'NEED_CORRECTION': 'Need Correction',
          'MAIL_SENT': 'Mail Sent',
          'PENDING': 'Pending',
          'PLACED': 'Placed',
          'LIVE': 'Live',
          'APPROVED': 'Approved',
          'FAILED': 'Failed',
        };
        return [
          { value: selectedPlacement.status, label: `${statusLabels[selectedPlacement.status] || selectedPlacement.status} (Current - Set by Admin)` },
          ...allowedStatuses
        ];
      }
    }

    return allowedStatuses;
  };

  // Clients get a limited set of statuses.
  const getClientStatuses = () => {
    const allowedStatuses = [
      { value: 'READY_TO_PUBLISH', label: 'Ready to Publish' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'FOR_REVIEW', label: 'For Review' },
      { value: 'NEED_CORRECTION', label: 'Need Correction' },
    ];

    // Keep any out-of-scope current status visible (read-only) so the dropdown
    // doesn't silently drop an admin-set value.
    if (selectedPlacement && selectedPlacement.status) {
      const exists = allowedStatuses.some(s => s.value === selectedPlacement.status);
      if (!exists) {
        const statusLabels: Record<string, string> = {
          MAIL_SENT: 'Mail Sent',
          SITE_REPLACED: 'Site Replaced',
          HOLD: 'Hold',
          PENDING: 'Pending',
          PLACED: 'Placed',
          LIVE: 'Live',
          APPROVED: 'Approved',
          FAILED: 'Failed',
        };
        return [
          { value: selectedPlacement.status, label: `${statusLabels[selectedPlacement.status] || selectedPlacement.status} (Current - Set by Admin)` },
          ...allowedStatuses,
        ];
      }
    }

    return allowedStatuses;
  };

  // Status label as the current user should see it.
  const statusDisplayLabel = (status?: string | null) => {
    if (!status) return '';
    if (status === 'FOR_REVIEW') return 'For Review';
    return status.replace(/_/g, ' ');
  };



  const handleClientFilterChange = (clientId: string) => {

    setClientFilter(clientId);

    setProjectFilter(''); // Clear project filter when client changes

  };



  if (!hasPlacementAccess) {

    return (

      <Box sx={{ p: 3 }}>

        <Alert severity="warning">

          Access denied. Only Admins, Publishers, and Clients can view orders.

        </Alert>

      </Box>

    );

  }



  const handleExportCSV = async () => {

    setExportingCSV(true);

    try {

      // Fetch ALL data with current filters (no pagination limit)

      const allDataResponse = await guestBlogPlacementService.getPlacements({

        limit: 10000, // Large limit to get all data

        clientId: clientFilter || undefined,

        projectId: projectFilter || undefined,

        publisherId: publisherFilter || undefined,

        search: search || undefined,

        status: statusFilter || undefined,

        month: monthFilter,

        year: yearFilter,

      });



      const allPlacements = allDataResponse.data;



      if (allPlacements.length === 0) {

        setSnackbar({

          open: true,

          message: 'No orders to export',

          severity: 'warning',

        });

        return;

      }



      // Build CSV data based on user role

      const csvData = allPlacements.map(placement => {

        const baseData: any = {

          placementId: placement.placementId || '',

          siteUrl: placement.guestBlogSite?.site_url || '',

        };

        // Add publisher name only for non-CLIENT users
        if (!isClient) {
          baseData.publisherName = placement.publishers?.publisherName || '';
        }

        // Add client and project only for non-publisher users
        if (!isPublisher) {
          // Only include client data for non-CLIENT users
          if (!isClient) {
            baseData.client = placement.client?.name || '';
          }
        }

        // Add project data for all users (ID for Publisher, name for others)
        baseData.project = isPublisher ? (placement.project?.id || '') : (placement.project?.projectName || '');

        // Add remaining fields
        return {
          ...baseData,
          price: formatCurrencyForCSV(isPublisher ? (placement.guestBlogSite?.base_price || placement.price) : placement.price),
          status: placement.status || '',
          liveUrl: placement.liveUrl || '',
          orderDate: formatDateForCSV(placement.orderDate),
          createdAt: formatDateForCSV(placement.createdAt),
        };

      });



      // Build columns based on user role

      const columns: Array<{ key: string; label: string }> = [

        { key: 'placementId', label: 'Placement ID' },

        { key: 'siteUrl', label: 'Site URL' },

      ];

      // Add publisher name column only for non-CLIENT users

      if (!isClient) {

        columns.push({ key: 'publisherName', label: 'Publisher Name' });

      }



      // Add client and project columns for non-publisher users, but only project for publishers

      if (!isPublisher) {

        // Only include client column for non-CLIENT users

        if (!isClient) {

          columns.push({ key: 'client', label: 'Client' });

        }

        columns.push({ key: 'project', label: 'Project' });

      } else {

        // For Publisher users, show only project ID

        columns.push({ key: 'project', label: 'Project' });

      }



      // Add remaining columns

      columns.push(

        { key: 'price', label: 'Price' },

        { key: 'status', label: 'Status' },

        { key: 'liveUrl', label: 'Live URL' },

        { key: 'orderDate', label: 'Order Date' },

        { key: 'createdAt', label: 'Created At' }

      );



      const statusLabel = statusFilter ? 

        guestBlogPlacementService.getAvailableStatuses().find(s => s.value === statusFilter)?.label || 'filtered' :

        'all';

      const filename = `placements_${statusLabel}_${new Date().toISOString().split('T')[0]}.csv`;

      exportToCSV(csvData, columns, filename);

      

      setSnackbar({

        open: true,

        message: `Exported ${allPlacements.length} orders to CSV`,

        severity: 'success',

      });

    } catch (error) {

      console.error('Export error:', error);

      setSnackbar({

        open: true,

        message: 'Failed to export orders',

        severity: 'error',

      });

    } finally {

      setExportingCSV(false);

    }

  };



  return (

    <Box
      className={inWorkflowStatus ? 'wf-step-page' : undefined}
      sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}
    >

      {/* Page Header */}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>

        <Box>

          {inWorkflowStatus ? (
            <>
              <h1 className="wf-step-title">Order status</h1>
              <p className="wf-step-subtitle">
                Track publishing progress for orders in this project.
              </p>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: '#111827', lineHeight: 1.2 }}>

                Guest Blog Orders

              </Typography>

              <Typography sx={{ fontSize: '12.5px', color: '#6B7280', mt: 0.25, lineHeight: 1.5 }}>

                Track publishing status and manage all guest blog orders

              </Typography>
            </>
          )}

        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 0.375 }}>

          <ColumnVisibilityMenu
            columns={columns}
            onColumnToggle={toggleColumn}
            onResetDefaults={resetDefaults}
          />

          <Button

            onClick={handleExportCSV}

            disabled={exportingCSV || totalCount === 0}

            sx={{

              display: 'inline-flex',

              alignItems: 'center',

              gap: '5px',

              padding: '7px 13px',

              borderRadius: '6px',

              fontFamily: 'inherit',

              fontSize: '12.5px',

              fontWeight: 500,

              cursor: 'pointer',

              transition: 'all 0.14s',

              whiteSpace: 'nowrap',

              lineHeight: 1,

              background: '#FFFFFF',

              color: '#374151',

              border: '1px solid #E5E7EB',

              boxShadow: '0 1px 2px rgba(0,0,0,.05)',

              '&:hover': {

                background: '#F9FAFB',

                color: '#111827'

              },

              '&:disabled': {

                opacity: 0.5,

                cursor: 'not-allowed'

              }

            }}

          >

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', flexShrink: 0 }}>

              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>

              <polyline points="7 10 12 15 17 10"/>

              <line x1="12" y1="15" x2="12" y2="3"/>

            </svg>

            {exportingCSV ? 'Exporting...' : 'Export as CSV'}

          </Button>

          {canImportOrders && !inWorkflowStatus && (
            <Button
              onClick={() => setOrderImportOpen(true)}
              title="Import orders from CSV or Excel"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 13px',
                borderRadius: '6px',
                fontFamily: 'inherit',
                fontSize: '12.5px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.14s',
                whiteSpace: 'nowrap',
                lineHeight: 1,
                background: '#FFFFFF',
                color: '#374151',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                '&:hover': {
                  background: '#F9FAFB',
                  color: '#111827',
                },
              }}
            >
              <FileUploadIcon sx={{ fontSize: 15 }} />
              Import Orders
            </Button>
          )}

        </Box>

      </Box>



      {/* Stats Strip — hidden: the per-status counts are already shown in the
          filter chips below, so the stat boxes looked like duplicated data. */}

      <Box sx={{ display: 'none', gridTemplateColumns: 'repeat(5, 1fr)', gap: '11px' }}>

        <Box sx={{

          background: '#FFFFFF',

          border: '1px solid #E5E7EB',

          borderRadius: '8px',

          padding: '13px 15px',

          boxShadow: '0 1px 2px rgba(0,0,0,.05)',

          display: 'flex',

          alignItems: 'center',

          gap: '11px',

          transition: 'box-shadow 0.18s, transform 0.18s',

          cursor: 'default',

          '&:hover': {

            boxShadow: '0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04)',

            transform: 'translateY(-1px)'

          }

        }}>

          <Box sx={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#F3F4F6' }}>

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '15px', height: '15px', color: '#6B7280' }}>

              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>

              <circle cx="12" cy="10" r="3"/>

            </svg>

          </Box>

          <Box>

            <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: '#111827' }}>

              {allPlacements.length}

            </Typography>

            <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: '3px', whiteSpace: 'nowrap' }}>

              Total orders

            </Typography>

          </Box>

        </Box>

        <Box sx={{

          background: '#FFFFFF',

          border: '1px solid #E5E7EB',

          borderRadius: '8px',

          padding: '13px 15px',

          boxShadow: '0 1px 2px rgba(0,0,0,.05)',

          display: 'flex',

          alignItems: 'center',

          gap: '11px',

          transition: 'box-shadow 0.18s, transform 0.18s',

          cursor: 'default',

          '&:hover': {

            boxShadow: '0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04)',

            transform: 'translateY(-1px)'

          }

        }}>

          <Box sx={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#EEF2FF' }}>

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '15px', height: '15px', color: '#4F46E5' }}>

              <circle cx="12" cy="12" r="10"/>

              <polyline points="12 6 12 12 16 14"/>

            </svg>

          </Box>

          <Box>

            <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: '#111827' }}>

              {allPlacements.filter(p => p.status === 'READY_TO_PUBLISH').length}

            </Typography>

            <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: '3px', whiteSpace: 'nowrap' }}>

              Ready to Publish

            </Typography>

          </Box>

        </Box>

        <Box sx={{

          background: '#FFFFFF',

          border: '1px solid #E5E7EB',

          borderRadius: '8px',

          padding: '13px 15px',

          boxShadow: '0 1px 2px rgba(0,0,0,.05)',

          display: 'flex',

          alignItems: 'center',

          gap: '11px',

          transition: 'box-shadow 0.18s, transform 0.18s',

          cursor: 'default',

          '&:hover': {

            boxShadow: '0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04)',

            transform: 'translateY(-1px)'

          }

        }}>

          <Box sx={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#FFF7ED' }}>

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '15px', height: '15px', color: '#EA580C' }}>

              <circle cx="12" cy="12" r="10"/>

              <line x1="12" y1="8" x2="12" y2="12"/>

              <line x1="12" y1="16" x2="12.01" y2="16"/>

            </svg>

          </Box>

          <Box>

            <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: '#111827' }}>

              {allPlacements.filter(p => p.status === 'FOR_REVIEW').length}

            </Typography>

            <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: '3px', whiteSpace: 'nowrap' }}>

              For Review

            </Typography>

          </Box>

        </Box>

        <Box sx={{

          background: '#FFFFFF',

          border: '1px solid #E5E7EB',

          borderRadius: '8px',

          padding: '13px 15px',

          boxShadow: '0 1px 2px rgba(0,0,0,.05)',

          display: 'flex',

          alignItems: 'center',

          gap: '11px',

          transition: 'box-shadow 0.18s, transform 0.18s',

          cursor: 'default',

          '&:hover': {

            boxShadow: '0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04)',

            transform: 'translateY(-1px)'

          }

        }}>

          <Box sx={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#FEF2F2' }}>

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '15px', height: '15px', color: '#DC2626' }}>

              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>

            </svg>

          </Box>

          <Box>

            <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: '#111827' }}>

              {allPlacements.filter(p => p.status === 'NEED_CORRECTION').length}

            </Typography>

            <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: '3px', whiteSpace: 'nowrap' }}>

              Need Correction

            </Typography>

          </Box>

        </Box>

        <Box sx={{

          background: '#FFFFFF',

          border: '1px solid #E5E7EB',

          borderRadius: '8px',

          padding: '13px 15px',

          boxShadow: '0 1px 2px rgba(0,0,0,.05)',

          display: 'flex',

          alignItems: 'center',

          gap: '11px',

          transition: 'box-shadow 0.18s, transform 0.18s',

          cursor: 'default',

          '&:hover': {

            boxShadow: '0 4px 12px rgba(0,0,0,.07), 0 2px 4px rgba(0,0,0,.04)',

            transform: 'translateY(-1px)'

          }

        }}>

          <Box sx={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#ECFDF5' }}>

            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '15px', height: '15px', color: '#059669' }}>

              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>

              <polyline points="22 4 12 14.01 9 11.01"/>

            </svg>

          </Box>

          <Box>

            <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: '#111827' }}>

              {allPlacements.filter(p => p.status === 'COMPLETED').length}

            </Typography>

            <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: '3px', whiteSpace: 'nowrap' }}>

              Completed

            </Typography>

          </Box>

        </Box>

      </Box>



      {/* Filter Bar */}

      <Box sx={{

        background: '#FFFFFF',

        border: '1px solid #E5E7EB',

        borderRadius: '8px',

        boxShadow: '0 1px 2px rgba(0,0,0,.05)',

        padding: '11px 14px',

        display: 'flex',

        alignItems: 'center',

        gap: '8px',

        flexWrap: 'wrap'

      }}>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '6px 10px', minWidth: '230px', transition: 'all 0.15s', '&:focus-within': { background: '#FFFFFF', borderColor: '#4F46E5', boxShadow: '0 0 0 3px rgba(79,70,229,.08)' } }}>

          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: '#9CA3AF', flexShrink: 0 }}>

            <circle cx="11" cy="11" r="8"/>

            <line x1="21" y1="21" x2="16.65" y2="16.65"/>

          </svg>

          <input

            type="text"

            placeholder={isPublisher ? "Search by Site URL, Domain..." : "Search by Site URL, Domain, Client, Project..."}

            value={search}

            onChange={(e) => setSearch(e.target.value)}

            style={{

              border: 'none',

              background: 'none',

              outline: 'none',

              fontFamily: 'inherit',

              fontSize: '12.5px',

              color: '#111827',

              width: '100%'

            }}

          />

        </Box>

        <Box sx={{ width: '1px', height: '18px', background: '#E5E7EB', flexShrink: 0 }}></Box>

        <select

          value={monthFilter || ''}

          onChange={(e) => setMonthFilter(e.target.value ? Number(e.target.value) : undefined)}

          style={{

            appearance: 'none',

            border: '1px solid #E5E7EB',

            borderRadius: '6px',

            padding: '5px 24px 5px 9px',

            fontFamily: 'inherit',

            fontSize: '12.5px',

            color: '#374151',

            background: '#F9FAFB',

            outline: 'none',

            cursor: 'pointer',

            transition: 'all 0.14s',

            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

            backgroundRepeat: 'no-repeat',

            backgroundPosition: 'right 7px center'

          }}

        >

          <option value="">All Months</option>

          <option value={1}>January</option>

          <option value={2}>February</option>

          <option value={3}>March</option>

          <option value={4}>April</option>

          <option value={5}>May</option>

          <option value={6}>June</option>

          <option value={7}>July</option>

          <option value={8}>August</option>

          <option value={9}>September</option>

          <option value={10}>October</option>

          <option value={11}>November</option>

          <option value={12}>December</option>

        </select>

        <select

          value={yearFilter || ''}

          onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : undefined)}

          style={{

            appearance: 'none',

            border: '1px solid #E5E7EB',

            borderRadius: '6px',

            padding: '5px 24px 5px 9px',

            fontFamily: 'inherit',

            fontSize: '12.5px',

            color: '#374151',

            background: '#F9FAFB',

            outline: 'none',

            cursor: 'pointer',

            transition: 'all 0.14s',

            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

            backgroundRepeat: 'no-repeat',

            backgroundPosition: 'right 7px center'

          }}

        >

          <option value="">All Years</option>

          {yearOptions.map((year) => (

            <option key={year} value={year}>

              {year}

            </option>

          ))}

        </select>

        {!isPublisher && !isClient && !inWorkflowStatus && (

          <select

            value={clientFilter}

            onChange={(e) => handleClientFilterChange(e.target.value)}

            style={{

              appearance: 'none',

              border: '1px solid #E5E7EB',

              borderRadius: '6px',

              padding: '5px 24px 5px 9px',

              fontFamily: 'inherit',

              fontSize: '12.5px',

              color: '#374151',

              background: '#F9FAFB',

              outline: 'none',

              cursor: 'pointer',

              transition: 'all 0.14s',

              minWidth: '110px',

              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

              backgroundRepeat: 'no-repeat',

              backgroundPosition: 'right 7px center'

            }}

          >

            <option value="">Client</option>

            {clients.map((client: any) => (

              <option key={client.id} value={client.id}>

                {client.name}

              </option>

            ))}

          </select>

        )}

        <select

          value={projectFilter}

          onChange={(e) => setProjectFilter(e.target.value)}

          style={{

            appearance: 'none',

            border: '1px solid #E5E7EB',

            borderRadius: '6px',

            padding: '5px 24px 5px 9px',

            fontFamily: 'inherit',

            fontSize: '12.5px',

            color: '#374151',

            background: '#F9FAFB',

            outline: 'none',

            cursor: 'pointer',

            transition: 'all 0.14s',

            minWidth: '145px',

            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

            backgroundRepeat: 'no-repeat',

            backgroundPosition: 'right 7px center'

          }}

        >

          <option value="">Project</option>

          {projects.map((project: any) => (

            <option key={project.id} value={project.id}>

              {isPublisher ? project.id : project.projectName}

            </option>

          ))}

        </select>

        {isSuperAdminFallback && (

          <select

            value={publisherFilter}

            onChange={(e) => setPublisherFilter(e.target.value)}

            style={{

              appearance: 'none',

              border: '1px solid #E5E7EB',

              borderRadius: '6px',

              padding: '5px 24px 5px 9px',

              fontFamily: 'inherit',

              fontSize: '12.5px',

              color: '#374151',

              background: '#F9FAFB',

              outline: 'none',

              cursor: 'pointer',

              transition: 'all 0.14s',

              minWidth: '130px',

              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

              backgroundRepeat: 'no-repeat',

              backgroundPosition: 'right 7px center'

            }}

          >

            <option value="">Publisher</option>

            {publishers.map((publisher: any) => (

              <option key={publisher.id} value={publisher.id}>

                {publisher.publisherName}

              </option>

            ))}

          </select>

        )}

        <Box sx={{ flex: 1 }}></Box>

        <button

          onClick={() => {

            setSearch('');

            setClientFilter('');

            setProjectFilter('');

            setPublisherFilter('');

            setMonthFilter(undefined);

            setYearFilter(undefined);

            setStatusFilter('READY_TO_PUBLISH');

          }}

          style={{

            display: 'inline-flex',

            alignItems: 'center',

            gap: '4px',

            fontSize: '12px',

            fontWeight: 500,

            color: '#9CA3AF',

            background: 'none',

            border: 'none',

            cursor: 'pointer',

            fontFamily: 'inherit',

            padding: '4px 6px',

            borderRadius: '6px',

            transition: 'all 0.12s'

          }}

          onMouseOver={(e) => {

            e.currentTarget.style.background = '#FEF2F2';

            e.currentTarget.style.color = '#DC2626';

          }}

          onMouseOut={(e) => {

            e.currentTarget.style.background = 'none';

            e.currentTarget.style.color = '#9CA3AF';

          }}

        >

          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px' }}>

            <line x1="18" y1="6" x2="6" y2="18"/>

            <line x1="6" y1="6" x2="18" y2="18"/>

          </svg>

          Clear

        </button>

      </Box>



      {/* Status Tab Chips */}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>

        <Box

          onClick={() => setStatusFilter('')}

          sx={{

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === '' ? '#F9FAFB' : '#FFFFFF',

            color: statusFilter === '' ? '#374151' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151',

            },

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#9CA3AF' }} />

          All

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === '' ? '#E5E7EB' : '#F3F4F6',

            color: statusFilter === '' ? '#6B7280' : '#9CA3AF',

            lineHeight: 1.6,

          }}>

            {allPlacements.length}

          </span>

        </Box>

        <Box

          onClick={() => setStatusFilter('READY_TO_PUBLISH')}

          sx={{

            order: isClient ? 1 : 0,

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: statusFilter === 'READY_TO_PUBLISH' ? 600 : 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'READY_TO_PUBLISH' ? '#EEF2FF' : '#FFFFFF',

            color: statusFilter === 'READY_TO_PUBLISH' ? '#4F46E5' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: statusFilter === 'READY_TO_PUBLISH' ? '#4338CA' : '#374151',

            },

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#4F46E5' }} />

          Ready to Publish

          <span style={{

            fontSize: '11px',

            fontWeight: 600,

            padding: '2px 6px',

            borderRadius: '99px',

            background: statusFilter === 'READY_TO_PUBLISH' ? '#DBEAFE' : '#F3F4F6',

            color: statusFilter === 'READY_TO_PUBLISH' ? '#1E40AF' : '#9CA3AF',

            lineHeight: 1.6,

          }}>

            {allPlacements.filter(p => p.status === 'READY_TO_PUBLISH').length}

          </span>

        </Box>

        <Box

          onClick={() => setStatusFilter('FOR_REVIEW')}

          sx={{

            order: isClient ? 3 : 0,

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'FOR_REVIEW' ? '#FFF7ED' : '#FFFFFF',

            color: statusFilter === 'FOR_REVIEW' ? '#EA580C' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151'

            }

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#EA580C' }}></span>

          For Review

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === 'FOR_REVIEW' ? '#FED7AA' : '#F3F4F6',

            color: statusFilter === 'FOR_REVIEW' ? '#EA580C' : '#9CA3AF',

            lineHeight: 1.6

          }}>

            {allPlacements.filter(p => p.status === 'FOR_REVIEW').length}

          </span>

        </Box>

        <Box

          onClick={() => setStatusFilter('NEED_CORRECTION')}

          sx={{

            order: isClient ? 4 : 0,

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'NEED_CORRECTION' ? '#FEF2F2' : '#FFFFFF',

            color: statusFilter === 'NEED_CORRECTION' ? '#DC2626' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151'

            }

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#DC2626' }}></span>

          Need Correction

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === 'NEED_CORRECTION' ? '#FECACA' : '#F3F4F6',

            color: statusFilter === 'NEED_CORRECTION' ? '#B91C1C' : '#9CA3AF',

            lineHeight: 1.6

          }}>

            {allPlacements.filter(p => p.status === 'NEED_CORRECTION').length}

          </span>

        </Box>

        <Box

          onClick={() => setStatusFilter('COMPLETED')}

          sx={{

            order: isClient ? 2 : 0,

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'COMPLETED' ? '#ECFDF5' : '#FFFFFF',

            color: statusFilter === 'COMPLETED' ? '#059669' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151'

            }

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#059669' }}></span>

          Completed

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === 'COMPLETED' ? '#A7F3D0' : '#F3F4F6',

            color: statusFilter === 'COMPLETED' ? '#047857' : '#9CA3AF',

            lineHeight: 1.6

          }}>

            {allPlacements.filter(p => p.status === 'COMPLETED').length}

          </span>

        </Box>

        {!isClient && (
        <>
        <Box

          onClick={() => setStatusFilter('MAIL_SENT')}

          sx={{

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'MAIL_SENT' ? '#F5F3FF' : '#FFFFFF',

            color: statusFilter === 'MAIL_SENT' ? '#7C3AED' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151'

            }

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#7C3AED' }}></span>

          Mail Sent

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === 'MAIL_SENT' ? '#DDD6FE' : '#F3F4F6',

            color: statusFilter === 'MAIL_SENT' ? '#7C3AED' : '#9CA3AF',

            lineHeight: 1.6

          }}>

            {allPlacements.filter(p => p.status === 'MAIL_SENT').length}

          </span>

        </Box>

        <Box

          onClick={() => setStatusFilter('SITE_REPLACED')}

          sx={{

            display: 'inline-flex',

            alignItems: 'center',

            gap: '5px',

            padding: '5px 12px 5px 10px',

            borderRadius: '99px',

            fontSize: '12px',

            fontWeight: 500,

            cursor: 'pointer',

            border: '1.5px solid #E5E7EB',

            background: statusFilter === 'SITE_REPLACED' ? '#FEFCE8' : '#FFFFFF',

            color: statusFilter === 'SITE_REPLACED' ? '#CA8A04' : '#6B7280',

            boxShadow: '0 1px 2px rgba(0,0,0,.05)',

            transition: 'all 0.14s',

            whiteSpace: 'nowrap',

            userSelect: 'none',

            '&:hover': {

              borderColor: '#D1D5DB',

              color: '#374151'

            }

          }}

        >

          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: '#CA8A04' }}></span>

          Site Replaced

          <span style={{

            fontSize: '10.5px',

            fontWeight: 700,

            marginLeft: '2px',

            padding: '0px 5px',

            borderRadius: '99px',

            background: statusFilter === 'SITE_REPLACED' ? '#FEF08A' : '#F3F4F6',

            color: statusFilter === 'SITE_REPLACED' ? '#CA8A04' : '#9CA3AF',

            lineHeight: 1.6

          }}>

            {allPlacements.filter(p => p.status === 'SITE_REPLACED').length}

          </span>

        </Box>

        <Box
          onClick={() => setStatusFilter('HOLD')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1.5px solid #E5E7EB',
            background: statusFilter === 'HOLD' ? '#F3E8FF' : '#FFFFFF',
            color: statusFilter === 'HOLD' ? '#7C3AED' : '#6B7280',
            boxShadow: '0 1px 2px rgba(0,0,0,.05)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '13px',
            fontWeight: 500,
            '&:hover': {
              background: statusFilter === 'HOLD' ? '#E9D5FF' : '#F9FAFB',
              borderColor: statusFilter === 'HOLD' ? '#C084FC' : '#D1D5DB',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0,0,0,.1)'
            }
          }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: statusFilter === 'HOLD' ? '#9CA3AF' : '#9CA3AF' }}></span>
          Hold
          <span style={{
            fontSize: '11px',
            fontWeight: 700,
            marginLeft: '2px',
            padding: '0px 5px',
            borderRadius: '99px',
            background: statusFilter === 'HOLD' ? '#E9D5FF' : '#F3F4F6',
            color: statusFilter === 'HOLD' ? '#7C3AED' : '#9CA3AF',
            lineHeight: 1.6
          }}>
            {allPlacements.filter(p => p.status === 'HOLD').length}
          </span>
        </Box>
        </>
        )}

      </Box>



      {/* Table */}

      <Box className="sites-table-container" sx={{ animation: 'fadeUp 0.22s ease forwards' }}>

        {/* Table Toolbar */}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>

          <Typography sx={{ fontSize: '12.5px', color: '#6B7280' }}>

            Showing <span style={{ color: '#111827', fontWeight: 600 }}>{placements.length}</span> placement{placements.length !== 1 ? 's' : ''}

          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#9CA3AF' }}>

            Sort by:

            <select
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value as PlacementSortOption);
                setPage(0);
              }}
              style={{

              border: '1px solid #E5E7EB',

              borderRadius: '5px',

              padding: '4px 20px 4px 7px',

              fontFamily: 'inherit',

              fontSize: '12px',

              color: '#374151',

              background: '#FFFFFF',

              outline: 'none',

              cursor: 'pointer',

              appearance: 'none',

              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke%2Dlinecap=\'round\' stroke%2Dlinejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',

              backgroundRepeat: 'no-repeat',

              backgroundPosition: 'right 5px center'

            }}>

              <option value="createdAt_desc">Created (Newest)</option>

              <option value="price_desc">GB Price (High → Low)</option>

              <option value="orderDate_desc">Order Month</option>

              <option value="status_asc">Status</option>

            </select>

          </Box>

        </Box>



        {/* Table Content */}

        <Box sx={{ overflowX: 'auto' }}>

          <table className="sites-table" style={{ width: '100%', borderCollapse: 'collapse' }}>

            <thead>

              <tr>

                {showActionsColumn && (
                <th style={{ 

                  textAlign: 'left', 

                  fontSize: '10.5px', 

                  fontWeight: 600, 

                  color: '#9CA3AF',

                  letterSpacing: '0.5px', 

                  textTransform: 'uppercase', 

                  padding: '9px 12px',

                  borderBottom: '1px solid #E5E7EB', 

                  background: '#FAFAFA',

                  whiteSpace: 'nowrap', 

                  cursor: 'pointer', 

                  userSelect: 'none',

                  position: 'sticky', 

                  top: 0, 

                  zIndex: 10, 

                  transition: 'color 0.1s',

                  width: '88px'

                }}>

                  Actions

                </th>
                )}

                {isColumnVisible('orderId') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    Order ID

                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>

                      <polyline points="6 9 12 15 18 9"/>

                    </svg>

                  </th>
                )}

                {isColumnVisible('siteUrl') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    Site URL

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>

                    <polyline points="6 9 12 15 18 9"/>

                  </svg>

                  </th>
                )}

                {isColumnVisible('content') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

}}>

Content

</th>
)}

{isColumnVisible('liveUrl') && (
<th style={{ 

textAlign: 'left', 

fontSize: '10.5px', 

fontWeight: 600, 

color: '#9CA3AF',

letterSpacing: '0.5px', 

textTransform: 'uppercase', 

padding: '9px 12px',

borderBottom: '1px solid #E5E7EB', 

background: '#FAFAFA',

whiteSpace: 'nowrap', 

cursor: 'pointer', 

userSelect: 'none',

position: 'sticky', 

top: 0, 

zIndex: 10, 

transition: 'color 0.1s'

}}>

Live URL

</th>
)}

{isColumnVisible('client') && !isPublisher && !isClient && (
<th style={{ 

textAlign: 'left', 

fontSize: '10.5px', 

fontWeight: 600, 

color: '#9CA3AF',

letterSpacing: '0.5px', 

textTransform: 'uppercase', 

padding: '9px 12px',

borderBottom: '1px solid #E5E7EB', 

background: '#FAFAFA',

whiteSpace: 'nowrap', 

cursor: 'pointer', 

userSelect: 'none',

position: 'sticky', 

top: 0, 

zIndex: 10, 

transition: 'color 0.1s'

}}>

Client

</th>
)}

{isColumnVisible('project') && (
<th style={{ 

textAlign: 'left', 

                  fontSize: '10.5px', 

                  fontWeight: 600, 

                  color: '#9CA3AF',

                  letterSpacing: '0.5px', 

                  textTransform: 'uppercase', 

                  padding: '9px 12px',

                  borderBottom: '1px solid #E5E7EB', 

                  background: '#FAFAFA',

                  whiteSpace: 'nowrap', 

                  cursor: 'pointer', 

                  userSelect: 'none',

                  position: 'sticky', 

                  top: 0, 

                  zIndex: 10, 

                  transition: 'color 0.1s'

                }}>

                  Project

                  </th>
                )}

                {isColumnVisible('orderMonth') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    Order Month

                  </th>
                )}

                {isColumnVisible('publisherName') && isSuperAdminFallback && (

                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    Publisher Name

                  </th>

                )}

                {isColumnVisible('price') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    GB Price

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>

                    <polyline points="18 15 12 9 6 15"/>

                  </svg>

                  </th>
                )}

                {isColumnVisible('status') && (
                  <th style={{ 

                    textAlign: 'left', 

                    fontSize: '10.5px', 

                    fontWeight: 600, 

                    color: '#9CA3AF',

                    letterSpacing: '0.5px', 

                    textTransform: 'uppercase', 

                    padding: '9px 12px',

                    borderBottom: '1px solid #E5E7EB', 

                    background: '#FAFAFA',

                    whiteSpace: 'nowrap', 

                    cursor: 'pointer', 

                    userSelect: 'none',

                    position: 'sticky', 

                    top: 0, 

                    zIndex: 10, 

                    transition: 'color 0.1s'

                  }}>

                    Status

                  </th>
                )}

                <th style={{ 

                  textAlign: 'left', 

                  fontSize: '10.5px', 

                  fontWeight: 600, 

                  color: '#9CA3AF',

                  letterSpacing: '0.5px', 

                  textTransform: 'uppercase', 

                  padding: '9px 12px',

                  borderBottom: '1px solid #E5E7EB', 

                  background: '#FAFAFA',

                  whiteSpace: 'nowrap', 

                  cursor: 'pointer', 

                  userSelect: 'none',

                  position: 'sticky', 

                  top: 0, 

                  zIndex: 10, 

                  transition: 'color 0.1s',

                  paddingRight: '16px'

                }}>

                  Created

                </th>

              </tr>

            </thead>

            <tbody>

              {placements.length === 0 ? (

                <tr>

                  <td colSpan={(isSuperAdminFallback ? 12 : 10) - (showActionsColumn ? 0 : 1)} align="center" style={{ padding: '72px 24px', fontSize: '13px', borderBottom: 'none' }}>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', textAlign: 'center' }}>

                      <Box sx={{ width: '64px', height: '64px', background: '#F9FAFB', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5, border: '1px solid #E5E7EB' }}>

                        <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" width="80" height="60" style={{ width: '26px', height: '26px', color: '#D1D5DB' }}>

                          <circle cx="28" cy="24" r="14" stroke="#D1D5DB" strokeWidth="2.5" fill="#F9FAFB"/>

                          <circle cx="28" cy="24" r="6" stroke="#D1D5DB" strokeWidth="2" fill="#F3F4F6"/>

                          <line x1="38" y1="34" x2="52" y2="48" stroke="#D1D5DB" strokeWidth="3" strokeLinecap="round"/>

                          <path d="M50 20 L56 14 M56 14 L62 20 M56 14 L56 30" stroke="#E5E7EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>

                        </svg>

                      </Box>

                      <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827', letterSpacing: '-0.2px' }}>

                        <span style={{ fontSize: '19px', fontWeight: 600, color: '#1F2937', fontFamily: 'JetBrains Mono, monospace' }}>

                          {totalCount}

                        </span>

                      </Typography>

                      <Typography sx={{ fontSize: '13px', color: '#6B7280', maxWidth: '340px', lineHeight: 1.7 }}>

                        No guest blog orders match your current filters. Try adjusting the status tab, month, or search terms.

                      </Typography>

                      <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>

                        orders are created automatically when orders are marked as published.

                      </Typography>

                    </Box>

                  </td>

                </tr>

              ) : (

                placements.map((placement) => (

                  <tr key={placement.id} style={{ transition: 'background 0.08s' }}>

                    {showActionsColumn && (
                    <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <div style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>

                        {canEditPlacements && (
                        <button

                          onClick={() => handleEdit(placement)}

                          style={{

                            width: '26px',

                            height: '26px',

                            borderRadius: '5px',

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'center',

                            cursor: 'pointer',

                            border: 'none',

                            background: 'none',

                            transition: 'all 0.1s'

                          }}

                          onMouseOver={(e) => {

                            e.currentTarget.style.background = '#FFFBEB';

                            const svg = e.currentTarget.querySelector('svg');

                            if (svg) svg.style.color = '#D97706';

                          }}

                          onMouseOut={(e) => {

                            e.currentTarget.style.background = 'none';

                            const svg = e.currentTarget.querySelector('svg');

                            if (svg) svg.style.color = '#9CA3AF';

                          }}

                          title="Edit"

                        >

                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: '#9CA3AF' }}>

                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>

                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>

                          </svg>

                        </button>
                        )}

                        {canEditPlacements && (
                        <button

                          onClick={() => handleArchive(placement)}

                          style={{

                            width: '26px',

                            height: '26px',

                            borderRadius: '5px',

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'center',

                            cursor: 'pointer',

                            border: 'none',

                            background: 'none',

                            transition: 'all 0.1s'

                          }}

                          onMouseOver={(e) => {

                            e.currentTarget.style.background = '#FFFBEB';

                            const svg = e.currentTarget.querySelector('svg');

                            if (svg) svg.style.color = '#D97706';

                          }}

                          onMouseOut={(e) => {

                            e.currentTarget.style.background = 'none';

                            const svg = e.currentTarget.querySelector('svg');

                            if (svg) svg.style.color = '#9CA3AF';

                          }}

                          title="Archive"

                        >

                          <ArchiveIconSvg size={12} color="#9CA3AF" />

                        </button>
                        )}

                      </div>

                    </td>
                    )}

                    {isColumnVisible('orderId') && (
                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', fontWeight: 500, color: '#6B7280' }}>
                          {placement.originalOrderNumber || placement.placementId}
                        </span>
                      </td>
                    )}

                    {isColumnVisible('siteUrl') && (
                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <a 

                        href={placement.guestBlogSite?.site_url?.startsWith('http') ? placement.guestBlogSite.site_url : `https://${placement.guestBlogSite?.site_url}`}

                        target="_blank"

                        rel="noopener noreferrer"

                        style={{ color: '#4F46E5', fontSize: '12.5px', fontWeight: 450, textDecoration: 'none', display: 'block', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}

                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}

                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}

                        title={placement.guestBlogSite?.site_url}

                      >

                        {placement.guestBlogSite?.site_url}

                      </a>

                      </td>
                    )}

                    {isColumnVisible('content') && (
                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      {(() => {
                        const contentUrl = getPlacementContentUrl(placement);
                        if (contentUrl) {
                          return (
                            <Tooltip title="View content document">
                              <IconButton
                                size="small"
                                component="a"
                                href={contentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ color: '#4F46E5' }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                        if (placement.contentText) {
                          return <span style={{ color: '#9CA3AF' }}>—</span>;
                        }
                        return (
                        <span style={{ 

                          display: 'inline-flex', 

                          alignItems: 'center', 

                          gap: '4px', 

                          padding: '4px 10px', 

                          borderRadius: '99px', 

                          fontSize: '11px', 

                          fontWeight: 600, 

                          background: '#F3F4F6', 

                          color: '#6B7280', 

                          border: '1px solid #E5E7EB' 

                        }}>

                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px', flexShrink: 0 }}>

                            <line x1="18" y1="6" x2="6" y2="18"/>

                            <line x1="6" y1="6" x2="18" y2="18"/>

                          </svg>

                          No

                        </span>
                        );
                      })()}

                      </td>
                    )}

                    {isColumnVisible('liveUrl') && (
                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      {placement.liveUrl ? (

                        <a 

                          href={placement.liveUrl.startsWith('http') ? placement.liveUrl : `https://${placement.liveUrl}`}

                          target="_blank"

                          rel="noopener noreferrer"

                          style={{ color: '#0D9488', fontSize: '12px', textDecoration: 'none', display: 'block', maxWidth: '135px', overflow: 'hidden', textOverflow: 'ellipsis' }}

                          onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}

                          onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}

                          title={placement.liveUrl}

                        >

                          {placement.liveUrl}

                        </a>

                      ) : (

                        <span style={{ fontSize: '13px', color: '#D1D5DB' }}>—</span>

                      )}

                      </td>
                    )}

                    {isColumnVisible('client') && !isPublisher && !isClient && (

                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                        <span style={{ fontSize: '12.5px', fontWeight: 500, color: '#111827', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>

                          {placement.client?.name}

                        </span>

                      </td>

                    )}

                    {isColumnVisible('project') && (
                      <td style={{ padding: '9px 12px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <span style={{ fontSize: '12px', color: '#6B7280', maxWidth: '115px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>

                        {isPublisher ? placement.project?.id : placement.project?.projectName}

                      </span>

                      </td>
                    )}

                    {isColumnVisible('orderMonth') && (
                      <td style={{ padding: '9px 12px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <span style={{ fontSize: '12px', color: '#374151' }}>

                        {placement.orderDate ? new Date(placement.orderDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}

                      </span>

                      </td>
                    )}

                    {isColumnVisible('publisherName') && isSuperAdminFallback && (

                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                        <span style={{ fontSize: '12.5px', color: '#111827' }}>

                          {placement.publishers?.publisherName}

                        </span>

                      </td>

                    )}

                    {isColumnVisible('price') && (
                      <td style={{ padding: '9px 12px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', fontWeight: 500, color: '#374151' }}>

                        ${isPublisher ? (placement.guestBlogSite?.base_price || placement.price) : placement.price}

                      </span>

                      </td>
                    )}

                    {isColumnVisible('status') && (
                      <td style={{ padding: '9px 12px', fontSize: '12.5px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>

                      <span style={{ 

                        display: 'inline-flex', 

                        alignItems: 'center', 

                        gap: '4px', 

                        padding: '3px 9px', 

                        borderRadius: '99px', 

                        fontSize: '11px', 

                        fontWeight: 600, 

                        whiteSpace: 'nowrap',

                        background: 

                          placement.status === 'READY_TO_PUBLISH' ? '#EEF2FF' :

                          placement.status === 'FOR_REVIEW' ? '#FFF7ED' :

                          placement.status === 'NEED_CORRECTION' ? '#FEF2F2' :

                          placement.status === 'COMPLETED' ? '#ECFDF5' :

                          placement.status === 'MAIL_SENT' ? '#F5F3FF' :

                          placement.status === 'SITE_REPLACED' ? '#FEFCE8' :

                          placement.status === 'HOLD' ? '#F3E8FF' :

                          '#F3F4F6',

                        color: 

                          placement.status === 'READY_TO_PUBLISH' ? '#4F46E5' :

                          placement.status === 'FOR_REVIEW' ? '#EA580C' :

                          placement.status === 'NEED_CORRECTION' ? '#DC2626' :

                          placement.status === 'COMPLETED' ? '#059669' :

                          placement.status === 'MAIL_SENT' ? '#7C3AED' :

                          placement.status === 'SITE_REPLACED' ? '#CA8A04' :

                          placement.status === 'HOLD' ? '#7C3AED' :

                          '#6B7280',

                        border: `1px solid ${

                          placement.status === 'READY_TO_PUBLISH' ? '#C7D2FE' :

                          placement.status === 'FOR_REVIEW' ? '#FED7AA' :

                          placement.status === 'NEED_CORRECTION' ? '#FECACA' :

                          placement.status === 'COMPLETED' ? '#A7F3D0' :

                          placement.status === 'MAIL_SENT' ? '#DDD6FE' :

                          placement.status === 'SITE_REPLACED' ? '#FEF08A' :

                          placement.status === 'HOLD' ? '#E9D5FF' :

                          '#E5E7EB'

                        }`

                      }}>

                        <span style={{ 

                          display: 'inline-block', 

                          width: '5px', 

                          height: '5px', 

                          borderRadius: '50%', 

                          flexShrink: 0,

                          background: 

                            placement.status === 'READY_TO_PUBLISH' ? '#4F46E5' :

                            placement.status === 'FOR_REVIEW' ? '#EA580C' :

                            placement.status === 'NEED_CORRECTION' ? '#DC2626' :

                            placement.status === 'COMPLETED' ? '#059669' :

                            placement.status === 'MAIL_SENT' ? '#7C3AED' :

                            placement.status === 'SITE_REPLACED' ? '#CA8A04' :

                            placement.status === 'HOLD' ? '#7C3AED' :

                            '#6B7280'

                        }}></span>

                        {statusDisplayLabel(placement.status)}

                      </span>

                      </td>
                    )}

                    <td style={{ padding: '9px 12px', fontSize: '12px', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle', whiteSpace: 'nowrap', paddingRight: '16px' }}>

                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: '#9CA3AF' }}>

                        {new Date(placement.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}

                      </span>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </Box>



        {/* Pagination */}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', padding: '9px 16px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>

            Rows per page:

            <select 

              value={rowsPerPage}

              onChange={(e) => handleRowsPerPageChange({ target: { value: e.target.value } } as any)}

              style={{

                border: '1px solid #E5E7EB',

                background: '#FFFFFF',

                borderRadius: '5px',

                padding: '3px 6px',

                fontFamily: 'inherit',

                fontSize: '12px',

                outline: 'none',

                cursor: 'pointer'

              }}

            >

              <option value={10}>10</option>

              <option value={25}>25</option>

              <option value={50}>50</option>

              <option value={100}>100</option>

            </select>

          </Box>

          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>

            {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}

          </span>

          <Box sx={{ display: 'flex', gap: '3px' }}>

            <button

              onClick={() => handlePageChange(null, Math.max(0, page - 1))}

              disabled={page === 0}

              style={{

                width: '26px',

                height: '26px',

                borderRadius: '5px',

                border: '1px solid #E5E7EB',

                background: '#FFFFFF',

                display: 'flex',

                alignItems: 'center',

                justifyContent: 'center',

                cursor: page === 0 ? 'not-allowed' : 'pointer',

                transition: 'all 0.1s',

                opacity: page === 0 ? 0.5 : 1

              }}

              onMouseOver={(e) => {

                if (page > 0) e.currentTarget.style.background = '#F9FAFB';

              }}

              onMouseOut={(e) => {

                e.currentTarget.style.background = '#FFFFFF';

              }}

            >

              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: '#374151' }}>

                <polyline points="15 18 9 12 15 6"/>

              </svg>

            </button>

            <button

              onClick={() => handlePageChange(null, page + 1)}

              disabled={(page + 1) * rowsPerPage >= totalCount}

              style={{

                width: '26px',

                height: '26px',

                borderRadius: '5px',

                border: '1px solid #E5E7EB',

                background: '#FFFFFF',

                display: 'flex',

                alignItems: 'center',

                justifyContent: 'center',

                cursor: (page + 1) * rowsPerPage >= totalCount ? 'not-allowed' : 'pointer',

                transition: 'all 0.1s',

                opacity: (page + 1) * rowsPerPage >= totalCount ? 0.5 : 1

              }}

              onMouseOver={(e) => {

                if ((page + 1) * rowsPerPage < totalCount) e.currentTarget.style.background = '#F9FAFB';

              }}

              onMouseOut={(e) => {

                e.currentTarget.style.background = '#FFFFFF';

              }}

            >

              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: '#374151' }}>

                <polyline points="9 18 15 12 9 6"/>

              </svg>

            </button>

          </Box>

        </Box>

      </Box>



      {/* Edit Dialog */}

      <Dialog

        open={editDialogOpen}

        onClose={() => setEditDialogOpen(false)}

        maxWidth="md"

        fullWidth

        PaperProps={{

          sx: {

            borderRadius: '12px',

            background: '#FFFFFF',

            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'

          }

        }}

      >

        <DialogTitle sx={{ 

          fontSize: '18px', 

          fontWeight: 600, 

          color: '#111827',

          borderBottom: '1px solid #E5E7EB',

          pb: 2

        }}>

          {isViewMode ? 'View Placement' : 'Edit Placement'}

        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>

          {selectedPlacement && (

            <Box>


              

              {/* Placement Info */}

              <Typography variant="body2" sx={{ mb: 0.5, color: '#6B7280' }}>

                Placement: <Typography component="span" sx={{ fontWeight: 600, color: '#111827' }}>{selectedPlacement.placementId}</Typography>

              </Typography>

              <Typography variant="body2" sx={{ mb: 3, color: '#6B7280' }}>

                Site: <Typography 

                  component="a"

                  href={selectedPlacement.guestBlogSite.site_url.startsWith('http') ? selectedPlacement.guestBlogSite.site_url : `https://${selectedPlacement.guestBlogSite.site_url}`}

                  target="_blank"

                  rel="noopener noreferrer"

                  sx={{

                    color: '#4F46E5',

                    textDecoration: 'none',

                    '&:hover': {

                      textDecoration: 'underline',

                    },

                  }}

                >

                  {selectedPlacement.guestBlogSite.site_url}

                </Typography>

              </Typography>



              {/* Content Requirements (hidden for all logins for now) */}

              {false && (
              <Box sx={{ mb: 3 }}>

                <Typography variant="subtitle2" sx={{ mb: 1.5, fontSize: '14px', fontWeight: 600, color: '#111827' }}>

                  Content Requirements

                </Typography>

                <RichTextEditor

                  value={editData.contentText}

                  onChange={(value) => setEditData({ ...editData, contentText: value })}

                  placeholder="Enter content requirements..."

                  disabled={isViewMode}

                />

              </Box>
              )}



              {/* Content URL */}

              <Box sx={{ mb: 3 }}>

                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>

                  Content URL (Google Docs, etc.)

                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

                  <TextField

                    fullWidth

                    value={editData.contentUrl}

                    onChange={(e) => setEditData({ ...editData, contentUrl: e.target.value })}

                    placeholder="https://docs.google.com/..."

                    size="small"

                    disabled={isViewMode}

                    sx={{

                      '& .MuiOutlinedInput-root': {

                        fontSize: '13px'

                      }

                    }}

                  />

                  {editData.contentUrl && (

                    <>

                      <Tooltip title="View Content URL">

                        <IconButton

                          size="small"

                          component="a"

                          href={editData.contentUrl}

                          target="_blank"

                          rel="noopener noreferrer"

                          sx={{ color: '#4F46E5' }}

                        >

                          <ViewIcon fontSize="small" />

                        </IconButton>

                      </Tooltip>

                      {canEditPlacements && !isViewMode && (
                      <Tooltip title="Edit Content URL">

                        <IconButton

                          size="small"

                          sx={{ color: '#6B7280' }}

                        >

                          <EditIcon fontSize="small" />

                        </IconButton>

                      </Tooltip>
                      )}

                    </>

                  )}

                </Box>

              </Box>



              {/* Content Document (hidden for all logins for now) */}

              {false && (
              <Box sx={{ mb: 3 }}>

                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>

                  Content Document

                </Typography>

                {editData.contentDocUrl ? (

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                    <Typography variant="body2" sx={{ color: '#059669', fontSize: '13px' }}>

                      ✓ Document uploaded

                    </Typography>

                    <Button

                      variant="text"

                      size="small"

                      onClick={() => setPreviewModalOpen(true)}

                      sx={{ color: '#4F46E5', fontSize: '12px', textTransform: 'none' }}

                    >

                      Preview

                    </Button>

                  </Box>

                ) : (

                  <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '13px', mb: 1.5 }}>

                    No document uploaded

                  </Typography>

                )}

                <Button

                  variant="outlined"

                  component="label"

                  startIcon={<AttachFileIcon />}

                  disabled={uploadingFile || isViewMode}

                  sx={{

                    textTransform: 'none',

                    fontSize: '13px',

                    color: '#4F46E5',

                    borderColor: '#4F46E5',

                    '&:hover': {

                      borderColor: '#4338CA',

                      background: '#EEF2FF'

                    }

                  }}

                >

                  {uploadingFile ? 'Uploading...' : 'Upload Document'}

                  <input

                    type="file"

                    hidden

                    accept=".pdf,.doc,.docx,.txt,.rtf"

                    onChange={handleFileUpload}

                  />

                </Button>

              </Box>
              )}



              {/* Status */}

              <Box sx={{ mb: 3 }}>

                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>

                  Status

                </Typography>

                <FormControl fullWidth size="small">

                  <Select

                    value={editData.status}

                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}

                    disabled={isViewMode}

                    sx={{

                      '& .MuiSelect-select': {

                        fontSize: '13px'

                      }

                    }}

                  >

                    {(isPublisher ? getPublisherStatuses() : isClient ? getClientStatuses() : getAllStatuses()).map((status) => (

                      <MenuItem key={status.value} value={status.value} sx={{ fontSize: '13px' }}>

                        {status.label}

                      </MenuItem>

                    ))}

                  </Select>

                </FormControl>

              </Box>



              {/* Live URL */}

              <Box sx={{ mb: 3 }}>

                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>

                  Live URL

                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

                  <TextField

                    fullWidth

                    value={editData.liveUrl}

                    onChange={(e) => setEditData({ ...editData, liveUrl: e.target.value })}

                    placeholder="https://example.com/live-post"

                    size="small"

                    disabled={isViewMode}

                    sx={{

                      '& .MuiOutlinedInput-root': {

                        fontSize: '13px'

                      }

                    }}

                  />

                  {editData.status === 'COMPLETED' && editData.liveUrl?.trim() && (
                    <Tooltip title="View Live URL">
                      <IconButton
                        size="small"
                        component="a"
                        href={normalizeLiveUrl(editData.liveUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#4F46E5' }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                </Box>

              </Box>



              {/* Notes */}

              <Box>

                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>

                  Notes

                </Typography>

                <TextField

                  fullWidth

                  value={editData.notes}

                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}

                  multiline

                  rows={3}

                  placeholder="Add any notes about this placement..."

                  size="small"

                  disabled={isViewMode}

                  sx={{

                    '& .MuiOutlinedInput-root': {

                      fontSize: '13px'

                    }

                  }}

                />

              </Box>

            </Box>

          )}

        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #E5E7EB' }}>

          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '14px' }}>

            {isViewMode ? 'Close' : 'Cancel'}

          </Button>

          {!isViewMode && (
          <Button 

            onClick={handleUpdate} 

            variant="contained" 

            disabled={updateMutation.isPending}

            sx={{ 

              textTransform: 'none', 

              fontSize: '14px',

              background: '#4F46E5',

              '&:hover': {

                background: '#4338CA'

              }

            }}

          >

            {updateMutation.isPending ? 'Updating...' : 'Update Placement'}

          </Button>
          )}

        </DialogActions>

      </Dialog>



      {/* Document Preview Modal */}

      {editData.contentDocUrl && (

        <DocumentPreviewModal

          open={previewModalOpen}

          onClose={() => setPreviewModalOpen(false)}

          documentUrl={editData.contentDocUrl}

          fileName={guestBlogPlacementService.getFileName(editData.contentDocUrl)}

          onDownload={() => guestBlogPlacementService.downloadFile(editData.contentDocUrl || '', `placement-${selectedPlacement?.placementId || 'content'}-content`)}

        />

      )}



      {/* Snackbar for notifications */}

      <Snackbar

        open={snackbar.open}

        autoHideDuration={6000}

        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}

      >

        <Alert

          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}

          severity={snackbar.severity}

          sx={{ width: '100%' }}

        >

          {snackbar.message}

        </Alert>

      </Snackbar>

      {isAdminFallback && !inWorkflowStatus && (
        <BulkOrderUploadModal
          isOpen={orderImportOpen}
          onClose={() => setOrderImportOpen(false)}
          onSuccess={(message) => {
            queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] });
            queryClient.invalidateQueries({ queryKey: ['guestBlogPlacements'] });
            queryClient.invalidateQueries({ queryKey: ['placements'] });
            queryClient.invalidateQueries({ queryKey: ['all-placements'] });
            setOrderImportOpen(false);
            setSnackbar({
              open: true,
              message: message || 'Bulk order import completed successfully!',
              severity: 'success',
            });
          }}
        />
      )}

      {/* Workflow quick-nav: sticky at the bottom while on the Order Status step */}
      {inWorkflowStatus && workflow && (
        <div className="wf-step-sticky-footer">
          <button type="button" className="wf-btn-sec" onClick={() => workflow.goToStep('overview')}>
            <OverviewIcon sx={{ fontSize: 16 }} />
            Project Overview
          </button>
          <button type="button" className="wf-btn-create" onClick={() => void handleExploreMoreSites()}>
            <LanguageIcon sx={{ fontSize: 16 }} />
            Explore more sites
          </button>
        </div>
      )}

    </Box>

  );

};



export default GuestBlogPlacementsPage;
